import Decimal from 'decimal.js';

const married_jointly_tax_slabs = [
  { limit: 23850, rate: 10 },
  { limit: 96950, rate: 12 },
  { limit: 206700, rate: 22 },
  { limit: 394600, rate: 24 },
  { limit: 501050, rate: 32 },
  { limit: 751600, rate: 35 },
  { limit: Infinity, rate: 37 },
];

function get_taxes_due(total_annual_taxable_income, tax_slabs_to_use = married_jointly_tax_slabs) {
  let taxes_due = new Decimal(0);
  const total_income_decimal = new Decimal(total_annual_taxable_income);

  for (let i = 0; i < tax_slabs_to_use.length; i++) {
    const slab = tax_slabs_to_use[i];

    if (i === 0) {
      if (total_income_decimal.gte(slab.limit)) {
        taxes_due = new Decimal(slab.rate).times(slab.limit).div(100);
      } else {
        taxes_due = new Decimal(slab.rate).times(total_income_decimal).div(100);
        return taxes_due;
      }
    } else { // i > 0
      let new_taxes_due = new Decimal(0);
      const full_slab_taxable = total_income_decimal.gt(slab.limit);
      const previous_slab_ceiling = new Decimal(tax_slabs_to_use[i - 1].limit);

      if (full_slab_taxable) {
        new_taxes_due = new Decimal(slab.rate).times(new Decimal(slab.limit).minus(previous_slab_ceiling)).div(100);
      } else {
        const taxable_amount = total_income_decimal.minus(previous_slab_ceiling);
        new_taxes_due = new Decimal(slab.rate).times(taxable_amount).div(100);
        taxes_due = taxes_due.plus(new_taxes_due);
        return taxes_due;
      }
      // This line should be here, outside the if/else for full_slab_taxable, but inside the else for i > 0
      taxes_due = taxes_due.plus(new_taxes_due);
    }
  }
  return taxes_due;
}

// For testing purposes
export const get_taxes_due_for_test = get_taxes_due;

export function calculate(globalSettings, people) {
  const {
    remaining_pay_cycles,
  } = globalSettings;

  const peopleWithNonTaxable = people.map(p => {
    let non_taxable_income = new Decimal(p.hsa_contribution || 0).plus(p.transit_contribution || 0).plus(p.dcfsa_contribution || 0);
    let four01k_contribution = new Decimal(p['401k_contribution'] || 0);
    let matched_amount = new Decimal(0);

    if (p['401k_match_type'] === 'Multiplier') {
      matched_amount = four01k_contribution.times(p['401k_match_value'] || 0);
    } else if (p['401k_match_type'] === 'Percentage') {
      const annual_income = new Decimal(p.annual_income || 0);
      const match_percentage = new Decimal(p['401k_match_value'] || 0).div(100);
      matched_amount = annual_income.times(match_percentage);
    } else if (p['401k_match_type'] === 'Fixed') {
      matched_amount = new Decimal(p['401k_match_value'] || 0);
    }

    if (p['401k_match_type'] === 'Multiplier') {
      non_taxable_income = non_taxable_income.plus(matched_amount);
    } else {
      non_taxable_income = non_taxable_income.plus(four01k_contribution).plus(matched_amount);
    }

    return { ...p, non_taxable_income: non_taxable_income.toNumber() };
  });

  const total_annual_income = peopleWithNonTaxable.reduce((sum, p) => sum.plus(p.annual_income || 0), new Decimal(0));
  const total_non_taxable_income = peopleWithNonTaxable.reduce((sum, p) => sum.plus(p.non_taxable_income || 0), new Decimal(0));
  const total_annual_taxable_income = total_annual_income.minus(total_non_taxable_income);

  const taxes_due = get_taxes_due(total_annual_taxable_income, married_jointly_tax_slabs);
  const expected_marginal_federal_tax_rate = total_annual_income.gt(0) ? taxes_due.div(total_annual_income).times(100) : new Decimal(0);
  const expected_marginal_federal_tax_rate_on_taxable_salary = total_annual_taxable_income.gt(0) ? taxes_due.div(total_annual_taxable_income).times(100) : new Decimal(0);

  const results = peopleWithNonTaxable.map(person => {
    const {
      annual_income: p_annual_income,
      total_income_till_now: p_total_income_till_now,
      total_taxable_income_till_now: p_total_taxable_income_till_now,
      federal_taxes_till_now: p_federal_taxes_till_now,
      yearly_base: p_yearly_base,
      non_taxable_income: p_non_taxable_income,
      regular_pay_till_now: p_regular_pay_till_now
    } = person;
    const [annual_income, total_income_till_now, total_taxable_income_till_now, federal_taxes_till_now, yearly_base, non_taxable_income, regular_pay_till_now] = [p_annual_income, p_total_income_till_now, p_total_taxable_income_till_now, p_federal_taxes_till_now, p_yearly_base, p_non_taxable_income, p_regular_pay_till_now].map(v => new Decimal(v || 0));

    // Regular pay is explicitly provided.
    // Supplemental income received so far (Bonus + Stock) is the difference
    const supplemental_income_till_now = total_income_till_now.minus(regular_pay_till_now);

    const person_remaining_pay_cycles = remaining_pay_cycles;
    const extra_withholdings = person.extra_withholdings ?? person.current_extra_withholdings;
    const supplemental_tax_rate = person.supplemental_tax_rate ?? person.current_supplemental_tax_rate ?? person.current_stock_supplemental_tax_rate;


    const federal_taxes_by_total_income_rate = total_income_till_now.gt(0) ? federal_taxes_till_now.div(total_income_till_now).times(100) : new Decimal(0);
    const federal_taxes_by_total_taxable_income_rate = total_taxable_income_till_now.gt(0) ? federal_taxes_till_now.div(total_taxable_income_till_now).times(100) : new Decimal(0);

    const expected_marginal_federal_tax_rate_person = annual_income.minus(non_taxable_income).gt(0) ? expected_marginal_federal_tax_rate.times(annual_income).div(100).div(annual_income.minus(non_taxable_income)).times(100) : new Decimal(0);

    const rate_difference = expected_marginal_federal_tax_rate_person.minus(federal_taxes_by_total_taxable_income_rate);
    const money_difference_till_now = rate_difference.times(total_taxable_income_till_now).div(100);

    const remaining_base_income = yearly_base.minus(regular_pay_till_now);

    // Total supplemental income expected for year = Annual Income - Annual Base
    const total_supplemental_income = annual_income.minus(yearly_base);
    // Remaining supplemental income = Total Expected Supplemental - Supplemental Received So Far
    const remaining_stock_income = total_supplemental_income.minus(supplemental_income_till_now);
    const remaining_income = remaining_base_income.plus(remaining_stock_income);

    const regular_pay_taxable_income = yearly_base.minus(non_taxable_income);
    const regular_pay_tax_rate = regular_pay_taxable_income.gt(0) ? get_taxes_due(regular_pay_taxable_income, married_jointly_tax_slabs).div(regular_pay_taxable_income).times(100) : new Decimal(0);

    const remaining_planned_taxes = new Decimal(person_remaining_pay_cycles).times(extra_withholdings).plus(regular_pay_tax_rate.times(remaining_base_income).div(100)).plus(remaining_stock_income.times(supplemental_tax_rate));
    const remaining_required_taxes = annual_income.minus(non_taxable_income).times(expected_marginal_federal_tax_rate_person).div(100).minus(federal_taxes_till_now);
    const extra_required_taxes = remaining_required_taxes.minus(remaining_planned_taxes);

    const deficit_till_now = money_difference_till_now.plus(extra_required_taxes);
    const deficit_per_remaining_pay_cycle = person_remaining_pay_cycles > 0 ? deficit_till_now.div(person_remaining_pay_cycles) : new Decimal(0);


    return {
      name: person.name,
      annual_income: annual_income.toNumber(),
      total_income_till_now: total_income_till_now.toNumber(),
      total_taxable_income_till_now: total_taxable_income_till_now.toNumber(),
      federal_taxes_till_now: federal_taxes_till_now.toNumber(),
      federal_taxes_by_total_income_rate: federal_taxes_by_total_income_rate.toNumber(),
      federal_taxes_by_total_taxable_income_rate: federal_taxes_by_total_taxable_income_rate.toNumber(),
      withholding_rate: supplemental_tax_rate,
      extra_withholdings: extra_withholdings,
      money_difference_till_now: money_difference_till_now.toNumber(),
      expected_marginal_federal_tax_rate_person: expected_marginal_federal_tax_rate_person.toNumber(),
      non_taxable_income: non_taxable_income.toNumber(),
      non_taxable_income_per_person: non_taxable_income.toNumber(), // This seems to be a misnomer in the original script, it's just the person's non_taxable_income
      regular_pay_taxable_income: regular_pay_taxable_income.toNumber(),
      remaining_income: remaining_income.toNumber(),
      remaining_base_income: remaining_base_income.toNumber(),
      remaining_stock_income: remaining_stock_income.toNumber(),
      regular_pay_tax_rate: regular_pay_tax_rate.toNumber(),
      remaining_planned_taxes: remaining_planned_taxes.toNumber(),
      remaining_required_taxes: remaining_required_taxes.toNumber(),
      extra_required_taxes: extra_required_taxes.toNumber(),
      deficit_till_now: deficit_till_now.toNumber(),
      deficit_per_remaining_pay_cycle: deficit_per_remaining_pay_cycle.toNumber(),
    };
  });

  return {
    people: results,
    total: {
      total_annual_income: total_annual_income.toNumber(),
      total_annual_taxable_income: total_annual_taxable_income.toNumber(),
      total_non_taxable_income: total_non_taxable_income.toNumber(),
      non_taxable_income_per_person: people.length > 0 ? total_non_taxable_income.div(people.length).toNumber() : 0,
      taxes_due: taxes_due.toNumber(),
      expected_marginal_federal_tax_rate: expected_marginal_federal_tax_rate.toNumber(),
      expected_marginal_federal_tax_rate_on_taxable_salary: expected_marginal_federal_tax_rate_on_taxable_salary.toNumber(),
    }
  }
}
